import type { ReactElement } from 'react'
import cc from 'classcat'

import BasketDiscountMessage, { findPotentialBasketDiscount } from '../../BasketDiscountMessage'
import {
  AmazonButton,
  CheckoutButton,
  PayPalCommerceButton,
  PaypalButton,
} from '../../templateComponents/Cart/CheckoutButtons'
import {
  renderPriceSavingsBasePriceWhenExists,
  renderPriceSavingsWhenQuantityIsReached,
} from '../../../utils/bulkPrices'
import { withComponents } from '../../../utils/translate'
import CartEmptyNote from '../epages.base/CartEmptyNote'
import CartTemplateComponent from '../../templateComponents/Cart/Cart'
import Coupons from '../../templateComponents/Cart/Coupons'
import EnergyLabel from '../../templateComponents/EnergyLabel'
import GaranLabel from '../../templateComponents/GaranLabel'
import LazyImage from '../../LazyImage'
import LineItem from '../../templateComponents/Cart/LineItem'
import LineItems from '../../templateComponents/Cart/LineItems'
import Link from '../../templateComponents/Link'
import PlaceholderImage from '../../PlaceholderImage'

export default function Cart({
  cart,
  displayWishlistError,
  trackProductClick,
  shop,
  t,
  invalidProducts,
  shopHasPickupAndShipping,
  cartHasShippingCosts,
}: CartThemeComponentProps): ReactElement {
  const basketDiscount = findPotentialBasketDiscount(shop.locale, cart.subAmountValue, cart.potentialBasketDiscounts)

  const someHasPriceSavings = (cart.productLineItems || []).some((lineItem) => {
    if (lineItem.lineItemCouponDiscount) return true
    if (!lineItem.additionalInformation?.bulkPriceInfo) return false
    return lineItem.additionalInformation?.bulkPriceInfo.some((info) => !!info.priceSavings)
  })

  const couponHasProductsAssigned = cart.productLineItems.some((product) => product.lineItemCouponDiscount)

  const renderPriceSavings = (locale: string, item: Core.ProductLineItem) =>
    renderPriceSavingsWhenQuantityIsReached(
      locale,
      item.additionalInformation?.bulkPriceInfo,
      item.quantity,
      item.lineItemCouponDiscount,
    )

  const cartHasCustomerGroupDiscount = cart.productLineItems.some((item) => Boolean(item.discount))
  const showDiscountColumn =
    cartHasCustomerGroupDiscount || cart.productLineItems.some((item) => Boolean(renderPriceSavings(shop.locale, item)))

  const renderDiscount = (lineItem: Core.ProductLineItem) => {
    if (cartHasCustomerGroupDiscount) {
      return lineItem.discount ? lineItem.discount.discountRate : null
    }
    return renderPriceSavings(shop.locale, lineItem)
  }

  const discountColumnLabel = showDiscountColumn
    ? t(
        cartHasCustomerGroupDiscount
          ? 'views.storefrontView.basketPageSection.discountColumnLabel.customerGroupDiscount'
          : 'views.storefrontView.basketPageSection.discountColumnLabel.couponDiscount',
      )
    : ''

  const removeCouponButtonLabel = t('views.storefrontView.basketPageSection.removeLineItemCouponButton.label')

  return (
    <CartTemplateComponent>
      <div>
        {!cart.totalNumberOfItems && (
          <div className="cart-empty">
            <CartEmptyNote shopHref={shop.href} t={t} />
          </div>
        )}

        {cart.totalNumberOfItems > 0 && (
          <div className="cart">
            <section className="section">
              <div className="wrapper">
                <div className="content">
                  <h1 className="cart-headline">{t('views.storefrontView.basketPageSection.title')}</h1>

                  <div aria-live="assertive" role="status">
                    {basketDiscount && <BasketDiscountMessage basketDiscountInfo={basketDiscount} t={t} />}

                    {cart.quantityMismatch && (
                      <div className="cart-warning-message">
                        {t('views.storefrontView.basketPageSection.productQuantityTooHighExplanation')}
                      </div>
                    )}
                    {cart.couponSuccess && !cart.coupon?.hasValidationErrors && (
                      <div className="cart-success-message">
                        {t('views.storefrontView.basketPageSection.couponInputField.successMessage')}
                      </div>
                    )}
                    {cart.checkoutState && !cart.checkoutState.priceValidToOrder && cart.minimumOrderValue && (
                      <div className="cart-warning-message">
                        {t('views.storefrontView.basketPageSection.checkoutValidation.cartMinimumAmountNotReached', {
                          amount: cart.minimumOrderValue.formatted,
                        })}
                      </div>
                    )}
                    {cart.coupon?.lineItemPrice?.formatted === '-' && cart.couponCampaign?.minimumOrderValue && (
                      <div className="cart-warning-message">
                        {t(
                          'views.storefrontView.basketPageSection.checkoutValidation.cartMinimumCouponAmountNotReached',
                          {
                            amount: cart.couponCampaign.minimumOrderValue.formatted,
                          },
                        )}
                        <Coupons.RemoveCouponButton className="cart-coupon-campaign-remove-coupon">
                          {t(
                            'views.storefrontView.basketPageSection.checkoutValidation.cartMinimumDiscountRemoveCoupon',
                          )}
                        </Coupons.RemoveCouponButton>
                      </div>
                    )}
                    {displayWishlistError && (
                      <div className="cart-warning-message">
                        {t('views.storefrontView.basketPageSection.wishlistItemsNotAddedCompletely')}
                      </div>
                    )}
                    {invalidProducts.length > 0 && (
                      <div className="cart-warning-message">
                        <span>
                          {t('views.storefrontView.basketPageSection.invalidProducts')}
                          <ul>
                            {invalidProducts.map((item) => (
                              <li key={`ip-${item.name}`}>{item.name}</li>
                            ))}
                          </ul>
                        </span>
                      </div>
                    )}
                    <Coupons.ValidationInfo errorClass="cart-error-message" warningClass="cart-warning-message" />

                    {cart.couponAutomaticallyRemoved && (
                      <div className="cart-warning-message">
                        {t('views.storefrontView.basketPageSection.couponAutomaticallyRemoved')}
                      </div>
                    )}
                  </div>

                  <table className="cart-table">
                    <thead className="cart-table-head">
                      <tr
                        className={cc({
                          'cart-table-item-has-discount-column': showDiscountColumn,
                        })}
                      >
                        <th className="cart-table-head-product">{t('components.productComponent.product')}</th>
                        <th className="cart-table-head-description visually-hidden">
                          {t('views.storefrontView.basketPageSection.descriptionColumn.accessibilityLabel')}
                        </th>
                        <th className="cart-table-head-price">
                          {t('views.storefrontView.basketPageSection.singlePrice')}
                        </th>
                        <th className="cart-table-head-quantity" id="cart-quantity-heading">
                          {t('views.storefrontView.basketPageSection.quantity')}
                        </th>
                        {showDiscountColumn && <th className="cart-table-head-discount">{discountColumnLabel}</th>}
                        <th className="cart-table-head-total">
                          {t('views.storefrontView.basketPageSection.totalPrice')}
                        </th>
                        <th className="cart-table-head-actions visually-hidden">
                          {t('views.storefrontView.basketPageSection.actionsColumn.accessibilityLabel')}
                        </th>
                      </tr>
                    </thead>
                    <LineItems
                      tagName="tbody"
                      renderItem={(lineItem, lineItemIndex) => (
                        <>
                          <tr
                            className={cc({
                              'cart-table-item-has-discount': someHasPriceSavings,
                              'cart-table-item-has-discount-column': showDiscountColumn,
                              'cart-table-item-has-deposit': lineItem.depositInfo,
                            })}
                          >
                            <td className="cart-table-item-product">
                              <Link
                                to={lineItem.productUrl}
                                onClick={() => trackProductClick(lineItem, lineItemIndex)}
                                // The product name has the same link and a useful text.
                                aria-hidden="true"
                                tabIndex={-1}
                              >
                                {lineItem.imageUrl ? (
                                  <LazyImage src={lineItem.imageUrl} className="cart-table-item-product-image" />
                                ) : (
                                  <PlaceholderImage className="cart-table-item-product-image" />
                                )}
                              </Link>
                            </td>
                            <td className="cart-table-item-description">
                              <h2 className="cart-table-item-description-headline" id={`${lineItemIndex}-product-name`}>
                                <Link
                                  to={lineItem.productUrl}
                                  onClick={() => trackProductClick(lineItem, lineItemIndex)}
                                >
                                  {lineItem.name}
                                </Link>
                              </h2>
                              {lineItem.variationString && (
                                <p className="cart-table-item-description-variation-values">
                                  {lineItem.variationString}
                                </p>
                              )}
                              {lineItem.customizedHeadlineText && lineItem.customizedText && (
                                <p className="cart-table-item-description-customized-text">
                                  <span>{lineItem.customizedHeadlineText}: </span>
                                  {lineItem.customizedText}
                                </p>
                              )}
                              {lineItem.essentialFeatures.length > 0 && (
                                <p className="cart-table-item-description-text">
                                  {lineItem.essentialFeatures.map((essentialFeature) => (
                                    <span key={essentialFeature}>
                                      {essentialFeature}
                                      <br />
                                    </span>
                                  ))}
                                </p>
                              )}
                              {lineItem.energyLabel && (
                                <EnergyLabel
                                  t={t}
                                  energyLabel={lineItem.energyLabel}
                                  energyLabelSourceFile={lineItem.energyLabelSourceFile}
                                />
                              )}

                              {lineItem.productDataSheet && (
                                <div className="cart-table-item-description-product-data-sheet">
                                  <a
                                    href={lineItem.productDataSheet.dataUri}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="cart-table-item-description-product-data-sheet-link"
                                  >
                                    {lineItem.productDataSheet.label}
                                  </a>
                                </div>
                              )}
                              {shop.isInEU && lineItem.garanLabel && <GaranLabel garanLabel={lineItem.garanLabel} />}

                              {!shop.beyond && lineItem.productId && (
                                <LineItem.AddToWishlistLinkInCart
                                  className={cc([
                                    'add-wishlist-link add-wishlist-icon',
                                    { 'is-in-wishlist': lineItem.isInWishlist },
                                  ])}
                                  title={t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                >
                                  <span className="add-wishlist-text">
                                    {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                  </span>
                                </LineItem.AddToWishlistLinkInCart>
                              )}

                              {couponHasProductsAssigned && lineItem.lineItemCouponDiscount && cart.couponCampaign && (
                                <>
                                  <p className="cart-table-item-description-coupon">
                                    <span>
                                      {t('views.storefrontView.basketPageSection.coupon')}: {cart.couponCampaign.name}
                                    </span>{' '}
                                    <span>({cart.couponCampaign.type.formatted})</span>
                                  </p>
                                  <p className="cart-table-item-description-coupon-remove-button">
                                    <Coupons.RemoveCouponButton
                                      className="remove-link remove-icon"
                                      title={removeCouponButtonLabel}
                                    >
                                      <span className="remove-text">{removeCouponButtonLabel}</span>
                                    </Coupons.RemoveCouponButton>
                                  </p>
                                </>
                              )}
                            </td>

                            <td className="cart-table-item-price">
                              <span className="show-subhead">
                                {t('views.storefrontView.basketPageSection.singlePrice')}
                              </span>
                              <span className="price-value">
                                {renderPriceSavingsBasePriceWhenExists(
                                  lineItem.additionalInformation?.bulkPriceInfo,
                                  lineItem.quantity,
                                  lineItem.orderUnitInfo,
                                  shop.locale,
                                ) ??
                                  lineItem.quantityPrice?.formatted ??
                                  lineItem.listPrice?.formatted ??
                                  lineItem.singleItemPrice?.formatted}
                              </span>
                            </td>
                            <td className="cart-table-item-quantity">
                              <span className="show-subhead">
                                {t('views.storefrontView.basketPageSection.quantity')}
                              </span>
                              <LineItem.QuantityField
                                min={0}
                                invalidProduct={lineItem.productId === null}
                                className="quantity-field"
                                aria-labelledby={`${lineItemIndex}-product-name cart-quantity-heading`}
                              />
                            </td>
                            {showDiscountColumn && (
                              <td className="cart-table-item-discount">
                                <span className="show-subhead">{discountColumnLabel}</span>
                                <span className="price-value">{renderDiscount(lineItem)}</span>
                              </td>
                            )}
                            <td className="cart-table-item-total">
                              <span className="show-subhead">
                                {t('views.storefrontView.basketPageSection.totalPrice')}
                              </span>
                              <span
                                className={cc(['price-value', { 'coupon-indicator': lineItem.lineItemCouponDiscount }])}
                              >
                                {lineItem.lineItemPrice.formatted}
                              </span>
                            </td>
                            <td className="cart-table-item-actions">
                              {!shop.beyond && lineItem.productId && (
                                <LineItem.AddToWishlistLinkInCart
                                  className={cc([
                                    'add-wishlist-link add-wishlist-icon',
                                    { 'is-in-wishlist': lineItem.isInWishlist },
                                  ])}
                                  title={t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                >
                                  <span className="add-wishlist-text">
                                    {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                  </span>
                                </LineItem.AddToWishlistLinkInCart>
                              )}

                              <LineItem.RemoveLineItemButton
                                className="remove-link remove-icon"
                                title={t('views.storefrontView.basketPageSection.removeProductButton.label')}
                              >
                                <span className="remove-text">
                                  {t('views.storefrontView.basketPageSection.removeProductButton.label')}
                                </span>
                              </LineItem.RemoveLineItemButton>
                            </td>
                          </tr>
                          {lineItem.depositInfo && (
                            <>
                              <tr
                                className={cc({
                                  'cart-table-item-deposit': true,
                                  'cart-table-item-has-discount-column': showDiscountColumn,
                                })}
                              >
                                <td className="cart-table-item-deposit-product"></td>
                                <td className="cart-table-item-deposit-label">
                                  {t('views.storefrontView.basketPageSection.deposit.label')}
                                </td>
                                <td className="cart-table-item-deposit-single-amount">
                                  <span className="show-subhead">
                                    {t('views.storefrontView.basketPageSection.deposit.single')}
                                  </span>
                                  <span className="price-value">{lineItem.depositInfo.single.formatted}</span>
                                </td>
                                <td className="cart-table-item-deposit-quantity cart-table-item-quantity">
                                  <span className="show-subhead">
                                    {t('views.storefrontView.basketPageSection.deposit.quantity')}
                                  </span>
                                  <div className="quantity-field">{lineItem.quantity}</div>
                                </td>
                                {showDiscountColumn && <td className="cart-table-item-deposit-discount"></td>}
                                <td className="cart-table-item-deposit-total-amount">
                                  <span className="show-subhead">
                                    {t('views.storefrontView.basketPageSection.deposit.total')}
                                  </span>
                                  <span>{lineItem.depositInfo.total.formatted}</span>
                                </td>
                              </tr>
                              <tr className="cart-table-item-actions-row">
                                <td className="cart-table-item-actions">
                                  {!shop.beyond && lineItem.productId && (
                                    <LineItem.AddToWishlistLinkInCart
                                      className={cc([
                                        'add-wishlist-link add-wishlist-icon',
                                        { 'is-in-wishlist': lineItem.isInWishlist },
                                      ])}
                                      title={t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                    >
                                      <span className="add-wishlist-text">
                                        {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                      </span>
                                    </LineItem.AddToWishlistLinkInCart>
                                  )}

                                  <LineItem.RemoveLineItemButton
                                    className="remove-link remove-icon"
                                    title={t('views.storefrontView.basketPageSection.removeProductButton.label')}
                                  >
                                    <span className="remove-text">
                                      {t('views.storefrontView.basketPageSection.removeProductButton.label')}
                                    </span>
                                  </LineItem.RemoveLineItemButton>
                                </td>
                              </tr>
                            </>
                          )}
                        </>
                      )}
                    ></LineItems>
                  </table>

                  {cart.canHaveCoupon && (
                    <Coupons.RedemptionForm className="coupon-redemption-form">
                      <label htmlFor="coupon-form-field" className="coupon-redemption-form-label">
                        {t('views.storefrontView.basketPageSection.couponInputField.label')}
                      </label>
                      <div>
                        <Coupons.RedemptionForm.CouponField
                          id="coupon-form-field"
                          className="coupon-redemption-form-field"
                          errorClass="invalid"
                        />
                        <Coupons.RedemptionForm.RedeemButton className="coupon-redemption-form-button">
                          <span>{t('views.storefrontView.basketPageSection.couponInputField.redeemButton.label')}</span>
                        </Coupons.RedemptionForm.RedeemButton>
                      </div>
                      <Coupons.CouponError
                        className="invalid-error-message"
                        hiddenClass="invalid-error-message-hidden"
                      />
                    </Coupons.RedemptionForm>
                  )}

                  <div className="cart-totals">
                    <table className="cart-totals-table">
                      <tbody>
                        <tr>
                          <td className="sub-total-title">{t('views.storefrontView.basketPageSection.subtotal')}</td>
                          <td className="sub-total">{cart.subAmount}</td>
                        </tr>
                        {cart.coupon && cart.couponCampaign && !couponHasProductsAssigned && (
                          <>
                            <tr className="cart-coupon">
                              <td className="coupon-title">
                                <div className="cart-coupon-campaign">
                                  <div className="cart-coupon-campaign-label">
                                    {t('views.storefrontView.basketPageSection.coupon')}
                                  </div>
                                  <span className="cart-coupon-campaign-name">
                                    {cart.couponCampaign.name}{' '}
                                    <span className="type">({cart.couponCampaign.type.formatted})</span>
                                  </span>
                                </div>
                              </td>
                              <td className="coupon-price">
                                {typeof cart.coupon.lineItemPrice.amount === 'number' && (
                                  <span className="cart-coupon-campaign-price">
                                    {cart.coupon.lineItemPrice.formatted}
                                  </span>
                                )}
                                <Coupons.RemoveCouponButton className="cart-coupon-campaign-remove-icon"></Coupons.RemoveCouponButton>
                              </td>
                            </tr>
                            <tr className="cart-coupon-campaign-remove-link">
                              <td>
                                <Coupons.RemoveCouponButton className="remove-link remove-icon">
                                  <span className="remove-text">
                                    {t('views.storefrontView.basketPageSection.couponInputField.removeButton.label')}
                                  </span>
                                </Coupons.RemoveCouponButton>
                              </td>
                              <td></td>
                            </tr>
                          </>
                        )}
                        {cart.shippingLineItem && (
                          <tr>
                            <td className="shipping-price-title">{cart.shippingLineItem.shippingMethod.name}</td>
                            <td className="shipping-price">{cart.shippingLineItem.lineItemPrice.formatted}</td>
                          </tr>
                        )}
                        {cart.totalBasketDiscount && (
                          <tr>
                            <td>{t('views.storefrontView.basketPageSection.discount')}</td>
                            <td>{cart.totalBasketDiscount}</td>
                          </tr>
                        )}
                        {cart.paymentLineItem && cart.paymentLineItem.lineItemPrice.amount !== 0 && (
                          <tr>
                            <td>
                              {cart.paymentLineItem.lineItemPrice.amount < 0
                                ? t('views.storefrontView.basketPageSection.paymentDiscount', {
                                    paymentMethodName: cart.paymentLineItem.paymentMethod.name,
                                  })
                                : t('views.storefrontView.basketPageSection.paymentSurcharge', {
                                    paymentMethodName: cart.paymentLineItem.paymentMethod.name,
                                  })}
                            </td>
                            <td>{cart.paymentLineItem.lineItemPrice.formatted}</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="grand-amount-title">{t('views.storefrontView.basketPageSection.total')}</td>
                          <td className="grand-amount">{cart.grandAmount}</td>
                        </tr>
                        <tr>
                          <td colSpan={2} className="grand-amount-note">
                            {cart.grandAmountNote && <span>{t(cart.grandAmountNote, { nsSeparator: '__' })}</span>}
                            {shopHasPickupAndShipping && !cartHasShippingCosts && (
                              <span>
                                {', '}
                                {withComponents(t)(
                                  'views.storefrontView.basketPageSection.priceInclusiveVatShippingCostHint.infoTextWithLink',
                                  {
                                    link: (
                                      <Link
                                        className="grand-amount-note-shipping-cost-link"
                                        to={shop.legalPageUrls.shipping}
                                      >
                                        {t(
                                          'views.storefrontView.basketPageSection.priceInclusiveVatShippingCostHint.linkText',
                                        )}
                                      </Link>
                                    ),
                                  },
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div id="ep-app-area-cart-view-cart-totals"></div>
                    <CheckoutButton className="cart-totals-button-checkout">
                      <span className="checkout-button-icon"></span>
                      {t('views.storefrontView.basketPageSection.checkoutLinkButton.label')}
                    </CheckoutButton>
                    <PayPalCommerceButton />
                    <div className="checkout-buttons column">
                      <PaypalButton className="paypal-button" shape="rect" color="gold" />
                      <AmazonButton className="amazon-button" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </CartTemplateComponent>
  )
}
